/* eslint-disable @typescript-eslint/return-await */

/* eslint-disable react/display-name */
import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { Col, Form, Input, Row } from "antd";
import { useStates } from "@/utils/address";
import { Validator } from "@/utils/validator";
import AddressInput from "@/components/AddressInput";
import EmailInput from "@/components/EmailInput";
import PhoneNumberInput, { flatPhoneNumber } from "@/components/PhoneNumberInput";

const UserForm = forwardRef(
  ({ states: propStates, loadingStates: propLoadingStates, children, isDealerOrGroup, allowAutofill = false }, ref) => {
    const formRef = useRef(null);
    const passwordInputRef = useRef();

    const validPropStates = useMemo(
      () => Array.isArray(propStates) || propLoadingStates,
      [propStates, propLoadingStates],
    );

    const { states: localStates, loading: localLoadingStates } = useStates(
      Array.isArray(propStates) || propLoadingStates,
    );

    const { states, loadingStates } = useMemo(() => {
      if (validPropStates) {
        return {
          states: propStates,
          loading: propLoadingStates,
        };
      }

      return {
        states: localStates,
        loading: localLoadingStates,
      };
    }, [localLoadingStates, localStates, propLoadingStates, propStates, validPropStates]);

    useImperativeHandle(
      ref,
      () => ({
        setFieldsValue(values) {
          formRef?.current?.setFieldsValue(values);
        },
        validateFields() {
          return formRef?.current?.validateFields();
        },
        resetFields() {
          formRef?.current?.resetFields();
        },
        async getUserData() {
          return await formRef.current.validateFields().then(values => {
            const payload = {
              ...values,
              phoneNumber: flatPhoneNumber(values.phoneNumber),
              shortStateName: values.stateShortName,
            };

            delete payload.stateShortName;

            return payload;
          });
        },
      }),
      [],
    );

    return (
      <Form validateTrigger="onBlur" layout="vertical" ref={formRef}>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item name="name" label="First Name" rules={[Validator.required("First Name is required")]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="surname" label="Last Name" rules={[Validator.required("Last Name is required")]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            {/* Fake email input added before the real one so that saved email/password
                will fill into here and ignore the real input when we don't want the real inputs filled
            */}
            {!allowAutofill && (
              <input
                name="!email"
                type="email"
                autoComplete="email"
                // Ensure this input is actually on the page so browser detects it, but don't affect real form inputs layout or show to the user
                style={{ position: "absolute", opacity: 0 }}
                // Ensure tabbing through inputs in form ignores this input
                tabIndex="-1"
              />
            )}
            <EmailInput formItemProps={{ required: true }} required />
          </Col>
          <Col xs={24} lg={12}>
            <PhoneNumberInput
              formItemProps={{
                name: "phoneNumber",
                label: "Phone Number",
                extensionName: "phoneNumberExtension",
                formRef,
                rules: [Validator.required("Phone Number is required")],
              }}
              nextInputRef={passwordInputRef}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            {!allowAutofill && (
              <input
                name="!password"
                type="password"
                autoComplete="password"
                style={{ position: "absolute", opacity: 0 }}
                tabIndex="-1"
              />
            )}
            <Form.Item name="password" label="Password" validateTrigger="onChange" rules={Validator.password()}>
              <Input.Password ref={passwordInputRef} style={{ borderRadius: 10 }} autoComplete="new-password" />
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={["password"]}
              hasFeedback
              validateTrigger="onChange"
              rules={[
                Validator.required("Please confirm your password"),
                Validator.custom(
                  value =>
                    value ===
                    (typeof formRef?.getFieldValue === "function"
                      ? formRef?.getFieldValue("password")
                      : formRef?.current?.getFieldValue("password")),
                  "The two passwords that you entered do not match",
                ),
              ]}
            >
              <Input.Password style={{ borderRadius: 10 }} autoComplete="off" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24}>
            <AddressInput
              formReference={formRef}
              loadingStates={loadingStates}
              states={states}
              addressLine1InputProps={{
                name: "addressLine1",
                label: "Address line 1",
              }}
              addressLine2InputProps={{
                name: "addressLine2",
                label: "Apt/Suite/Other",
              }}
              addressLine1FormItemProps={{
                name: "addressLine1",
                label: "Address line 1",
                rules: [
                  isDealerOrGroup
                    ? Validator.optional(Validator.required, "Address line 1 is required")
                    : Validator.required("Address line 1 is required"),
                ],
              }}
              stateIdFormItemProps={{
                name: "stateId",
                label: "State",
                rules: [
                  isDealerOrGroup
                    ? Validator.optional(Validator.required, "State is required")
                    : Validator.required("State is required"),
                ],
              }}
              stateNameFormItemProps={{
                name: "stateName",
                label: "State",
                rules: [
                  isDealerOrGroup
                    ? Validator.optional(Validator.required, "State is required")
                    : Validator.required("State is required"),
                ],
              }}
              stateShortNameFormItemProps={{
                name: "stateShortName",
                label: "State",
                rules: [
                  isDealerOrGroup
                    ? Validator.optional(Validator.required, "State is required")
                    : Validator.required("State is required"),
                ],
              }}
              cityFormItemProps={{
                name: "city",
                label: "City",
                rules: [
                  isDealerOrGroup
                    ? Validator.optional(Validator.required, "City is required")
                    : Validator.required("City is required"),
                ],
              }}
              zipCodeFormItemProps={{
                name: "zipCode",
                label: "Zip code",
                rules: [
                  isDealerOrGroup
                    ? Validator.optional(Validator.required, "Zip code is required")
                    : Validator.required("Zip code is required"),
                ],
              }}
            />
          </Col>
        </Row>
        {children}
      </Form>
    );
  },
);

export default UserForm;
